import { faPauseCircle, faPlayCircle, faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AsyncTypeahead } from "react-bootstrap-typeahead";
import { InputGroup } from "react-bootstrap";
import { CampaignRecord, CampaignStatus, IncludeExclude, AUTOCOMPLETE_ITEM_PADDING } from "../../../utils/types";
import { isAllowedFilterField } from "../../../utils/UserHelper";
import AutocompleteIncludeExclude from "../../library/AutocompleteIncludeExclude";
import "../../library/Typeahead.css";

interface CampaignsAutocompleteProps {
  onChange: (selected: string[]) => void; // Array of campaign IDs
  options: CampaignRecord[] | null;
  selected: string[]; // Array of campaign IDs
  reactRef: any;
  disabled?: boolean;
  multiple?: boolean;
  placeholder?: string;
  useIncludeExclude?: boolean;
  defaultIncludeExclude?: IncludeExclude;
  includeExcludeOnChange?: (newIncludeExclude:IncludeExclude) => void;
  style?: {[key:string]:string};
  onInputChange?: (query: string) => void;
  isLoading?: boolean;
}

const MIN_TYPEAHEAD_LENGTH = 3;

function CampaignsAutocomplete(props: CampaignsAutocompleteProps) {
  // Get options directly from CampaignRecord[], filtering out "All Campaigns" entries
  const getOptions = (): CampaignRecord[] => {
    if (!props.options) return [];
    // Filter out campaigns with campaignId === "***" (All Campaigns)
    return props.options.filter(campaign => campaign.campaignId !== '***');
  };

  // Convert selected campaign IDs to CampaignRecord objects
  // Create placeholder records for IDs not found in options (for custom entries like "tile.custom:us")
  const getSelectedOptions = (): CampaignRecord[] => {
    if (!props.selected || props.selected.length === 0) {
      return [];
    }

    return props.selected.map(id => {
      // Try to find the campaign in the options array
      const found = props.options?.find(c => c.campaignId === id);

      // If found, return it; otherwise create a placeholder
      if (found) {
        return found;
      }

      // Create placeholder CampaignRecord for custom/unknown campaign IDs
      return {
        campaignId: id,
        title: id, // Use the ID as the title since we don't have other info
        campaignStatus: undefined // Unknown status
      } as CampaignRecord;
    });
  };

  // Label function to display both ID and title
  const labelKeyFunction = (option: CampaignRecord) => {
    const title = option.title || '';
    return title ? `${option.campaignId} - ${title}` : option.campaignId || '';
  };

  // Handle selection changes
  const handleOnChange = (selected: CampaignRecord[]) => {
    // Extract just the campaign IDs
    const selectedIds = selected.map(option => option.campaignId);
    props.onChange(selectedIds);
  };

  // Bypass client-side filtering - results are already filtered by the search endpoint
  const filterBy = () => true;

  const renderMenuItemChildren = (campaign: CampaignRecord) => {
    let statusIcon = null;
    let color = "#000000";
    let tooltip = null;

    switch (campaign.campaignStatus) {
      case CampaignStatus.Paused:
        statusIcon = <FontAwesomeIcon icon={faPauseCircle} fixedWidth></FontAwesomeIcon>;
        color = "#cccccc";
        tooltip = "Paused. Campaign is currently (today) paused in Kepler system.";
        break;
      case CampaignStatus.Active:
        statusIcon = <FontAwesomeIcon icon={faPlayCircle} fixedWidth></FontAwesomeIcon>;
        color = "green";
        tooltip = "Active. Campaign is currently (today) active in Kepler system.";
        break;
      default:
        color = "#ccc";
        tooltip = "Unknown. Campaign is currently not defined in Kepler system.";
        statusIcon = <FontAwesomeIcon icon={faQuestionCircle} style={{ fontSize: "14px", color: "#9fcdff" }} fixedWidth></FontAwesomeIcon>
        break;
    }

    const title = campaign.title || '';
    const label = title ? `${campaign.campaignId} - ${title}` : campaign.campaignId;

    return (
      <div style={{
        display: "flex",
        flexDirection: "row",
        width: "100%",
        alignItems: "center",
        padding: AUTOCOMPLETE_ITEM_PADDING
      }}>
        <div style={{minWidth: "1.8em", textAlign: "center", color: color, fontSize: "120%"}} title={tooltip}>
          {statusIcon}
        </div>
        <div style={{
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}>
          {label}
        </div>
      </div>
    );
  };

  return (
    <InputGroup className="campaigns-autocomplete">
      {isAllowedFilterField("campaignIdsExclude") && (
        <AutocompleteIncludeExclude
          defaultIncludeExclude={props.defaultIncludeExclude}
          includeExcludeOnChange={props.includeExcludeOnChange}
          disabled={props.disabled}
        />
      )}
      <AsyncTypeahead
        id="campaignsAutocomplete"
        size="sm"
        filterBy={filterBy}
        isLoading={props.isLoading || false}
        labelKey={labelKeyFunction}
        minLength={MIN_TYPEAHEAD_LENGTH}
        onSearch={props.onInputChange || (() => {})}
        options={getOptions()}
        selected={getSelectedOptions()}
        onChange={handleOnChange}
        placeholder={`Type ${MIN_TYPEAHEAD_LENGTH}+ chars to search campaigns...`}
        renderMenuItemChildren={renderMenuItemChildren}
        multiple={props.multiple !== false}
        clearButton
        ref={props.reactRef}
        useCache={false}
        disabled={props.disabled}
      />
    </InputGroup>
  );
}

export default CampaignsAutocomplete;